import { useSearchParams } from 'react-router-dom';

import { getApiFilters } from '~/shared/utils/apiFilters';
import { stripAutoscalingFiltersFromApiFilters } from '~/shared/utils/autoscalingFilterUtils';
import { clearAllFilterSearchParams } from '~/shared/utils/filterUrlParams';
import { SelectOption } from '~/shared/utils/types';

import { apiFilterKey } from '../../../../common/serachParamKeys';
import {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  FilterStoredInURL,
  buildAutoscalingApiFilters,
  filterNonSpecificGroups,
  combineFilters,
} from '../utils/filterUtils';

function setAutoscalingUrlParams(
  updatedParams: URLSearchParams,
  stateIds: string[],
  eligibilityIds: string[]
) {
  if (stateIds.length > 0) {
    updatedParams.set(AUTOSCALING_STATE_PARAM_KEY, stateIds.join(','));
  } else {
    updatedParams.delete(AUTOSCALING_STATE_PARAM_KEY);
  }

  if (eligibilityIds.length > 0) {
    updatedParams.set(AUTOSCALING_ELIGIBILITY_PARAM_KEY, eligibilityIds.join(','));
  } else {
    updatedParams.delete(AUTOSCALING_ELIGIBILITY_PARAM_KEY);
  }
}

function buildFinalApiFilters(
  existingFiltersFromUrl: FilterStoredInURL[],
  stateIds: string[],
  eligibilityIds: string[],
  pendingClusterNameFilters: SelectOption[],
  pendingVolumeIdFilters: SelectOption[],
  pendingVolumeTagFilters: SelectOption[],
  pendingAccountFilters: SelectOption[],
  pendingInstanceFilters: SelectOption[]
): SelectOption[] {
  const baseApiFilters = filterNonSpecificGroups(existingFiltersFromUrl);
  const autoscalingApiFilters = buildAutoscalingApiFilters(stateIds, eligibilityIds);

  return combineFilters(
    baseApiFilters,
    pendingClusterNameFilters,
    pendingVolumeIdFilters,
    pendingVolumeTagFilters,
    pendingAccountFilters,
    pendingInstanceFilters,
    autoscalingApiFilters.filter((f) => f.group === 'autoscalingState'),
    autoscalingApiFilters.filter((f) => f.group === 'supported')
  );
}

function writeFilterParams(
  updatedParams: URLSearchParams,
  finalApiFilters: SelectOption[],
  stateIds: string[],
  eligibilityIds: string[]
) {
  setAutoscalingUrlParams(updatedParams, stateIds, eligibilityIds);

  if (finalApiFilters.length > 0) {
    updatedParams.set(apiFilterKey, JSON.stringify(finalApiFilters));
    const processedFilters = getApiFilters(finalApiFilters);
    updatedParams.set('processed_filters', JSON.stringify(processedFilters));
  } else {
    updatedParams.delete(apiFilterKey);
    updatedParams.delete('processed_filters');
  }
}

export function useFilterUrlParams() {
  const [_searchParams, setSearchParams] = useSearchParams();

  const applyFilters = (
    pendingAutoscaleState: string[],
    pendingEligibility: string[],
    pendingClusterNameFilters: SelectOption[],
    pendingVolumeIdFilters: SelectOption[],
    pendingVolumeTagFilters: SelectOption[],
    pendingAccountFilters: SelectOption[],
    pendingInstanceFilters: SelectOption[]
  ) => {
    setSearchParams(
      (prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);

        const existingFiltersFromUrl = JSON.parse(
          prevParams.get(apiFilterKey) || '[]'
        ) as FilterStoredInURL[];

        const finalApiFilters = buildFinalApiFilters(
          existingFiltersFromUrl,
          pendingAutoscaleState,
          pendingEligibility,
          pendingClusterNameFilters,
          pendingVolumeIdFilters,
          pendingVolumeTagFilters,
          pendingAccountFilters,
          pendingInstanceFilters
        );

        writeFilterParams(
          updatedParams,
          finalApiFilters,
          pendingAutoscaleState,
          pendingEligibility
        );

        return updatedParams;
      },
      { replace: true }
    );
  };

  const applyAutoscalingFiltersOnly = (stateIds: string[], eligibilityIds: string[]) => {
    setSearchParams(
      (prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);

        const existingFiltersFromUrl = JSON.parse(
          prevParams.get(apiFilterKey) || '[]'
        ) as FilterStoredInURL[];

        const filtersWithoutAutoscaling =
          stripAutoscalingFiltersFromApiFilters(existingFiltersFromUrl);
        const autoscalingApiFilters = buildAutoscalingApiFilters(stateIds, eligibilityIds);
        const finalApiFilters = [
          ...filtersWithoutAutoscaling,
          ...autoscalingApiFilters.filter((f) => f.group === 'autoscalingState'),
          ...autoscalingApiFilters.filter((f) => f.group === 'supported'),
        ];

        writeFilterParams(updatedParams, finalApiFilters, stateIds, eligibilityIds);

        return updatedParams;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchParams(
      (prev) => {
        const updatedParams = new URLSearchParams(prev);
        clearAllFilterSearchParams(updatedParams, apiFilterKey);
        return updatedParams;
      },
      { replace: true }
    );
  };

  return { applyFilters, applyAutoscalingFiltersOnly, resetFilters };
}
